import React from 'react';
import { Info, Coins, Calculator, PiggyBank, Zap, AlertTriangle, Percent, Shield, CreditCard, TrendingUp } from 'lucide-react';

/**
 * Компонент с информацией о вкладах и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-green-500" />
        Всё о банковских вкладах: виды, условия, советы
      </h2>
      
      <div className="prose prose-green dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-green-500" />
          Что такое банковский вклад?
        </h3>
        <p>
          <strong>Банковский вклад (депозит)</strong> — это денежные средства, которые физическое или юридическое лицо размещает в банке на определенный срок или до востребования под установленный процент. Это один из самых простых и надежных финансовых инструментов для сохранения и приумножения капитала.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-green-500" />
          Ключевые параметры вклада
        </h3>
        <ul>
          <li><strong>Сумма вклада:</strong> Размер денежных средств, которые вы размещаете в банке.</li>
          <li><strong>Процентная ставка:</strong> Размер вознаграждения, которое банк платит за пользование вашими деньгами (указывается в процентах годовых).</li>
          <li><strong>Срок вклада:</strong> Период, на который размещаются деньги (от нескольких дней до нескольких лет).</li>
          <li>
            <strong>Капитализация процентов:</strong> Начисление процентов не только на основную сумму, но и на уже выплаченные проценты.
            <ul>
              <li><strong>Без капитализации:</strong> Проценты выплачиваются отдельно, не увеличивая основную сумму.</li>
              <li><strong>С капитализацией:</strong> Проценты прибавляются к основной сумме, и в следующем периоде проценты начисляются уже на увеличенную сумму.</li>
            </ul>
          </li>
          <li><strong>Минимальная сумма вклада:</strong> Минимальный порог для открытия определенного вида вклада.</li>
          <li><strong>Возможность пополнения:</strong> Возможность вносить дополнительные суммы на вклад в течение срока его действия.</li>
          <li><strong>Возможность частичного снятия:</strong> Возможность снимать часть средств без потери процентов.</li>
          <li><strong>Условия досрочного закрытия:</strong> Правила и штрафы при досрочном расторжении договора.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-green-500" />
          Основные виды вкладов
        </h3>
        <ul>
          <li><strong>Срочный вклад:</strong> Размещается на определенный срок с фиксированной ставкой. Обычно имеет более высокую доходность, но ограничивает доступ к деньгам до окончания срока.</li>
          <li><strong>Вклад до востребования:</strong> Средства могут быть сняты в любое время, но процентная ставка минимальная.</li>
          <li><strong>Накопительный вклад:</strong> Предусматривает возможность пополнения и нацелен на постепенное накопление средств.</li>
          <li><strong>Сберегательный счет:</strong> Сочетает элементы вклада и текущего счета, обычно с возможностью снятия и пополнения.</li>
          <li><strong>Валютный вклад:</strong> Размещается в иностранной валюте (доллары, евро, юани и т.д.).</li>
          <li><strong>Мультивалютный вклад:</strong> Позволяет хранить средства в нескольких валютах одновременно и конвертировать между ними.</li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Капитализация процентов значительно увеличивает доходность
          </h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            При одинаковой ставке, вклад с ежемесячной капитализацией принесет заметно больший доход, чем вклад без капитализации, особенно на длительных сроках. Например, при ставке 8% годовых за 3 года вклад с ежемесячной капитализацией даст примерно на 12% больше дохода.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-500" />
          Система страхования вкладов
        </h3>
        <p>
          В России действует система страхования вкладов, управляемая Агентством по страхованию вкладов (АСВ). Если банк, входящий в систему, становится банкротом или у него отзывают лицензию, вкладчики получают страховое возмещение:
        </p>
        <ul>
          <li>100% суммы всех вкладов в банке, но не более 1,4 млн рублей в общей сложности;</li>
          <li>Для отдельных категорий вкладов (например, эскроу-счета при покупке недвижимости) страховое покрытие может быть выше;</li>
          <li>Страхованию подлежат средства физических лиц и индивидуальных предпринимателей.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Налогообложение доходов по вкладам
        </h3>
        <p>
          С 1 января 2021 года изменился порядок налогообложения процентов по вкладам. Теперь НДФЛ (13%) облагается доход, превышающий необлагаемый минимум, который рассчитывается по формуле: 1 млн рублей умноженный на ключевую ставку ЦБ на начало года.
        </p>
        <p>
          Например, при ключевой ставке 12% необлагаемый минимум дохода по вкладам составит 120 000 рублей в год. Если общий доход по всем вкладам превысит эту сумму, с превышения будет взиматься налог 13%.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при выборе вклада
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Внимательно читайте договор, особенно условия досрочного закрытия</li>
            <li>Уточняйте эффективную ставку с учетом капитализации</li>
            <li>Проверяйте наличие скрытых комиссий и сборов</li>
            <li>Узнайте, участвует ли банк в системе страхования вкладов</li>
            <li>Оцените удобство управления вкладом (наличие мобильного приложения, возможность дистанционного обслуживания)</li>
            <li>Сравните условия нескольких банков перед принятием решения</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-green-500" />
          Как правильно выбрать банк для размещения вклада
        </h3>
        <ul>
          <li><strong>Надежность банка:</strong> Проверьте рейтинги, репутацию и финансовую устойчивость.</li>
          <li><strong>Участие в системе страхования вкладов:</strong> Убедитесь, что банк входит в список участников СВС.</li>
          <li><strong>Условия вкладов:</strong> Сравните ставки, сроки, минимальные суммы и дополнительные опции.</li>
          <li><strong>Удобство обслуживания:</strong> Оцените наличие отделений, банкоматов, онлайн-банкинга.</li>
          <li><strong>Дополнительные преимущества:</strong> Некоторые банки предлагают бонусы, скидки, кэшбэк или повышенные ставки для постоянных клиентов.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          Стратегии максимизации доходности вкладов
        </h3>
        <ul>
          <li><strong>Распределение средств между несколькими банками:</strong> Для диверсификации рисков и максимизации страхового покрытия.</li>
          <li><strong>Лестница депозитов:</strong> Распределение средств между вкладами с разными сроками для баланса между доходностью и ликвидностью.</li>
          <li><strong>Капитализация процентов:</strong> Выбор вкладов с более частой капитализацией для увеличения итоговой доходности.</li>
          <li><strong>Регулярные пополнения:</strong> Систематическое добавление средств на пополняемые вклады для ускорения роста капитала.</li>
          <li><strong>Пролонгация вкладов:</strong> Автоматическое продление вклада на новый срок для сохранения выгодных условий.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор вкладов поможет вам спланировать сбережения, рассчитать потенциальный доход и выбрать оптимальные параметры для вашего вклада!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;